import { defineStore } from "pinia";
import { ref, computed, watch, nextTick } from "vue";
import router from "../router/index";
import {
  WORK_GUIDANCE_STEPS,
  WORK_GUIDANCE_TOTAL_STEPS,
  ONBOARDING_GUIDANCE_STEPS,
  ONBOARDING_GUIDANCE_TOTAL_STEPS,
} from "../common/constants/guidanceConfig";
import { getGuidanceProgress, saveGuidanceProgress } from "../api/guidanceApi";
import { checkGuidanceCondition } from "../common/helpers/guidanceHelpers";
import { useSingleSearchStore } from "./eplex/eplexSingleSearch";
import { useFunctionSettingsStore } from "./eplex/eplexFunctionSettings";
import { useClientContextStore } from "./eplex/eplexClientContext";
import type { GuidanceKind, GuidanceProgress, GuidanceStepConfig } from "../models/guidance/GuidanceTypes";

/**
 * В активной вкладке проценки на экране есть результаты. Именно это и означает
 * «первая проценка сделана» для шага 2: закрытая или чужая вкладка не считается
 * (CAB-636, замечание 8). Смотрим на сами результаты, а не на requestId: он
 * остаётся у вкладки и без выдачи (нужен корзине/заморозке) — по нему вторая
 * часть шага показывалась на пустом стартовом экране.
 */
function activeTabHasResults(): boolean {
  const store = useSingleSearchStore();
  const tabId = store.activeTabId;
  return tabId !== null && (store.searches[tabId]?.results?.length ?? 0) > 0;
}

function countConfiguredSuppliers(): number {
  return useFunctionSettingsStore().userPlugins.length;
}

const CONFIG: Record<GuidanceKind, { steps: GuidanceStepConfig[]; totalSteps: number }> = {
  work: { steps: WORK_GUIDANCE_STEPS, totalSteps: WORK_GUIDANCE_TOTAL_STEPS },
  onboarding: { steps: ONBOARDING_GUIDANCE_STEPS, totalSteps: ONBOARDING_GUIDANCE_TOTAL_STEPS },
};

export const useGuidanceStore = defineStore("guidance", () => {
  const clientContext = useClientContextStore();

  const progressByKind = ref<Record<GuidanceKind, GuidanceProgress>>({
    work: { step: 1, paused: false },
    onboarding: { step: 1, paused: false },
  });
  const isLoaded = ref(false);
  const hasLoadError = ref(false);
  const isFloatingExpanded = ref(false);
  const highlightedSidebarItems = ref<string[]>([]);
  const stopModalVisible = ref(false);
  const stopModalAction = ref<"pause" | "skipAll">("pause");
  const workLaunchRequested = ref(false);
  // Второе визуальное состояние текущего шага (см. GuidanceStepConfig.secondaryTargetSelector) —
  // страница сама сообщает о своём локальном событии, номер шага при этом не
  // меняется (ТЕСТ ОНБОРДИНГ §4/§5/§6-7): открылась карточка поставщика,
  // отмечена хотя бы одна строка в корзине.
  const secondaryHighlightActive = ref(false);

  // Пока онбординг не завершён и клиент либо ещё в демо, либо уже начал его
  // (не бросаем на середине при выходе в боевой) — общий оверлей/тултип/
  // плавающая кнопка показывают онбординг. Обучение по работе никуда не
  // девается: прогресс не трогается, страница «Обучение» (см. workProgress
  // и т.д. ниже) работает независимо от этого правила — просто гид-оверлей
  // не всплывает поверх онбординга (спека 2026-08-31, §2/§5/§6 — открытый
  // вопрос 10 документа задания решён так: активность турам не даём
  // одновременно, work не теряет прогресс, просто ждёт).
  const activeKind = computed<GuidanceKind>(() => {
    const onboarding = progressByKind.value.onboarding;
    const onboardingUnfinished = onboarding.step <= ONBOARDING_GUIDANCE_TOTAL_STEPS;
    const onboardingEligible = clientContext.isTrial === true || onboarding.step > 1;
    return onboardingUnfinished && onboardingEligible ? "onboarding" : "work";
  });

  const progress = computed<GuidanceProgress>({
    get: () => progressByKind.value[activeKind.value],
    set: (value) => {
      progressByKind.value = { ...progressByKind.value, [activeKind.value]: value };
    },
  });

  const totalSteps = computed(() => CONFIG[activeKind.value].totalSteps);

  const currentStepConfig = computed<GuidanceStepConfig | null>(() => {
    if (!isLoaded.value || hasLoadError.value || progress.value.paused) return null;
    const kind = activeKind.value;
    if (progress.value.step > CONFIG[kind].totalSteps) return null;

    const config = CONFIG[kind].steps[progress.value.step - 1];
    if (!config) return null;

    if (config.advanceMode === "welcome" || config.advanceMode === "finish") return config;

    if (!checkGuidanceCondition(config.condition)) return null;

    const currentRoute = router.currentRoute.value.path;
    if (config.route && config.route !== currentRoute) return null;

    if (config.secondaryTargetSelector && (readyToAdvance.value || secondaryHighlightActive.value)) {
      return {
        ...config,
        targetSelector: config.secondaryTargetSelector,
        textKey: config.secondaryTextKey ?? config.textKey,
        tooltipPosition: config.secondaryTooltipPosition ?? config.tooltipPosition,
        // Не наследуем hintKey первого состояния — призыв к действию у него
        // свой (или не нужен вовсе), а не «тот же, но для другой цели».
        hintKey: config.secondaryHintKey,
        hintIcon: config.secondaryHintIcon,
        tooltipOffsetY: config.secondaryTooltipOffsetY,
        highlightInForeground: config.secondaryHighlightInForeground,
      };
    }

    return config;
  });

  // Выпускник онбординга не должен тут же увидеть приветствие 29-шагового тура
  // работы — только по своей инициативе со страницы «Обучение» (ТЕСТ ОНБОРДИНГ,
  // §8). Отличаем его от давнего клиента, который никогда не касался онбординга
  // (там onboarding.step всегда 1, а не > total) — для него ничего не меняется:
  // тур работы всплывает сам, как и раньше.
  const isWorkAutoLaunchSuppressed = computed(() => {
    return (
      activeKind.value === "work" &&
      progressByKind.value.onboarding.step > ONBOARDING_GUIDANCE_TOTAL_STEPS &&
      progressByKind.value.work.step === 1 &&
      !progressByKind.value.work.paused &&
      !workLaunchRequested.value
    );
  });

  const isActive = computed(() => {
    if (isWorkAutoLaunchSuppressed.value) return false;
    return (
      isLoaded.value &&
      !hasLoadError.value &&
      !progress.value.paused &&
      progress.value.step <= CONFIG[activeKind.value].totalSteps
    );
  });

  const isModalStep = computed(() => {
    const config = currentStepConfig.value;
    return config?.advanceMode === "welcome" || config?.advanceMode === "finish";
  });

  const isComplete = computed(() => progress.value.step > CONFIG[activeKind.value].totalSteps);

  const allowedSidebarItems = computed<string[]>(() => {
    if (!isActive.value) return [];

    const config = CONFIG[activeKind.value].steps[progress.value.step - 1];
    return config?.allowedSidebarItems ?? [];
  });

  // Work-специфичные геттеры — для страницы «Обучение» (EplexGuidance.vue),
  // которая всегда про тур работы, независимо от того, что сейчас в activeKind.
  const workProgress = computed(() => progressByKind.value.work);
  const workTotalSteps = WORK_GUIDANCE_TOTAL_STEPS;
  const isWorkComplete = computed(() => progressByKind.value.work.step > WORK_GUIDANCE_TOTAL_STEPS);

  function navigateToCurrentStep() {
    const kind = activeKind.value;
    const current = progressByKind.value[kind];
    if (current.paused || current.step > CONFIG[kind].totalSteps) return;

    const config = CONFIG[kind].steps[current.step - 1];
    if (!config) return;

    const targetRoute = config.route ?? null;
    if (!targetRoute) return;

    const currentPath = router.currentRoute.value.path;
    if (currentPath !== targetRoute) {
      router.replace(targetRoute);
    }
  }

  async function init() {
    if (isLoaded.value) return;
    try {
      const serverProgress = await getGuidanceProgress();
      progressByKind.value = serverProgress;
      hasLoadError.value = false;
      navigateToCurrentStep();
    } catch (error) {
      console.error("Failed to load guidance progress:", error);
      hasLoadError.value = true;
    } finally {
      isLoaded.value = true;
    }
  }

  const canGoBack = computed(() => {
    if (progress.value.step <= 1) return false;

    // Единая кнопка на всех шагах онбординга (ТЕСТ ОНБОРДИНГ 2, п. «Назад») —
    // в отличие от тура работы, возврат на шаг 2 к приветственному экрану
    // тоже разрешён, а не только с шага 3 и далее.
    if (activeKind.value === "onboarding") return true;

    const prevConfig = CONFIG[activeKind.value].steps[progress.value.step - 2];
    return prevConfig?.advanceMode !== "welcome";
  });

  async function advance() {
    const kind = activeKind.value;
    if (progress.value.step > CONFIG[kind].totalSteps) return;

    const newStep = progress.value.step + 1;
    progress.value = { ...progress.value, step: newStep };

    // Ведём человека только на страницу шага (route). navigateOnClick — это куда он
    // попадёт САМ, кликнув по цели: если переходить туда заранее, наблюдатель за
    // маршрутом считает шаг выполненным и тур проскакивает его (шаг 2 тура работы —
    // клик по «Поставщикам» в меню — пропадал сразу после приветствия).
    navigateToStepRoute(CONFIG[kind].steps[newStep - 1]);

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to save guidance progress");
    }
  }

  function navigateToStepRoute(config: GuidanceStepConfig | undefined) {
    const targetRoute = config?.route ?? null;
    if (!targetRoute) return;
    if (router.currentRoute.value.path !== targetRoute) {
      router.replace(targetRoute);
    }
  }

  async function goBack() {
    const kind = activeKind.value;
    if (progress.value.step <= 1) return;

    const newStep = progress.value.step - 1;
    progress.value = { ...progress.value, step: newStep };

    navigateToStepRoute(CONFIG[kind].steps[newStep - 1]);

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to save guidance progress");
    }
  }

  async function pause() {
    if (activeKind.value === "onboarding") {
      // Онбординг не подразумевает «пауза, вернусь позже»: в меню его нет, а
      // страница «Обучение» работает только с туром работы (Task 4, спека
      // §4) — приостановленный онбординг был бы недостижим нигде в
      // интерфейсе. Пропуск считается пройденным — то же поведение, что и у
      // «Пропустить всё».
      await skipAll();
      return;
    }

    progress.value = { ...progress.value, paused: true };
    isFloatingExpanded.value = false;

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to save guidance pause state");
    }
  }

  async function resume() {
    progress.value = { ...progress.value, paused: false };

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to save guidance resume state");
    }
  }

  async function skipAll() {
    progress.value = { step: CONFIG[activeKind.value].totalSteps + 1, paused: false };
    isFloatingExpanded.value = false;

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to save guidance skip state");
    }
  }

  async function resetProgress() {
    progress.value = { step: 1, paused: false };

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to reset guidance progress");
    }
  }

  async function resumeWork() {
    progressByKind.value = { ...progressByKind.value, work: { ...progressByKind.value.work, paused: false } };

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to save guidance resume state");
    }
  }

  async function resetWorkProgress() {
    progressByKind.value = { ...progressByKind.value, work: { step: 1, paused: false } };
    workLaunchRequested.value = false;

    try {
      await saveGuidanceProgress(progressByKind.value);
    } catch {
      console.error("Failed to reset guidance progress");
    }
  }

  // Кнопка «Начать обучение» на странице «Обучение» — единственный способ для
  // выпускника онбординга показать тур работы (см. isWorkAutoLaunchSuppressed).
  // Флаг сессионный, на бэкенд не уходит: после первого реального шага
  // (work.step > 1) подавка и так перестаёт действовать сама по себе.
  function launchWork() {
    workLaunchRequested.value = true;
  }

  function setSecondaryHighlightActive(active: boolean) {
    secondaryHighlightActive.value = active;
  }

  function reset() {
    progressByKind.value = { work: { step: 1, paused: false }, onboarding: { step: 1, paused: false } };
    workLaunchRequested.value = false;
    secondaryHighlightActive.value = false;
    isLoaded.value = false;
    hasLoadError.value = false;
    isFloatingExpanded.value = false;
    highlightedSidebarItems.value = [];
    stopModalVisible.value = false;
    stopModalAction.value = "pause";
    readyToAdvance.value = false;
  }

  const stepEntryBaseline = ref<number | null>(null);
  // «Свободная зона» шага «Первая проценка» (см. разбор замечаний 25.08, B3/Q7):
  // requestId проценки приходит сразу после запуска поиска, задолго до реальных
  // результатов — если продвигать тур в этот момент, подложка шага 3 перекрывает
  // выдачу раньше, чем человек успел на неё посмотреть. Вместо автопродвижения
  // просто отмечаем «готово» — кнопка «Далее» в тултипе продвигает явно, когда
  // человек сам решит, что насмотрелся.
  const readyToAdvance = ref(false);

  watch(
    () => [activeKind.value, progress.value.step] as const,
    async ([, step]) => {
      readyToAdvance.value = false;

      const config = CONFIG[activeKind.value].steps[step - 1];
      // Шаг «Первая проценка» не про счётчик: его вторая часть показывается ровно
      // тогда, когда в активной вкладке открыты результаты, и уходит, когда их
      // закрыли — состояние держит watch ниже, здесь только начальное значение.
      if (config?.advanceCondition === "hasPerformedNewSearch") {
        stepEntryBaseline.value = null;
        await nextTick();
        await nextTick();
        if (progress.value.step !== step) return;
        readyToAdvance.value = activeTabHasResults();
        return;
      }
      if (config?.advanceCondition !== "hasAddedNewSupplier") {
        stepEntryBaseline.value = null;
        return;
      }

      // На входе в шаг (особенно сразу после полной перезагрузки страницы) стор
      // проценок/поставщиков ещё может быть пуст: EplexMultiSearch восстанавливает
      // его из localStorage асинхронно в своём onMounted, а guidanceStore.init()
      // получает прогресс с бэкенда независимо и может обогнать эту гидратацию.
      // Если снять baseline=0 до восстановления, а не после, счётчик восстановленных
      // (СТАРЫХ) проценок/поставщиков через мгновение подскочит выше baseline —
      // watch ниже примет это за «пользователь только что это сделал» и мгновенно
      // проскочит шаг без реального действия. Ждём кадр, чтобы гидратация успела
      // отработать первой.
      await nextTick();
      await nextTick();

      if (progress.value.step !== step) return; // шаг уже сменился, пока мы ждали

      stepEntryBaseline.value = countConfiguredSuppliers();
    },
    { immediate: true },
  );

  watch(
    () => {
      const config = CONFIG[activeKind.value].steps[progress.value.step - 1];
      if (!config?.advanceCondition) return false;
      if (config.advanceCondition === "hasPerformedNewSearch") {
        return activeTabHasResults();
      }
      if (config.advanceCondition === "hasAddedNewSupplier") {
        return stepEntryBaseline.value !== null && countConfiguredSuppliers() > stepEntryBaseline.value;
      }
      return checkGuidanceCondition(config.advanceCondition);
    },
    (conditionMet) => {
      const config = CONFIG[activeKind.value].steps[progress.value.step - 1];
      // «Свободная зона» шага 2 живёт и гаснет вместе с результатами в активной
      // вкладке: закрыли проценку — шаг снова просит её сделать.
      if (config?.advanceCondition === "hasPerformedNewSearch") {
        readyToAdvance.value = conditionMet;
        return;
      }
      if (!conditionMet) return;

      advance();
    },
  );

  watch(
    () => router.currentRoute.value.path,
    (newPath) => {
      const config = CONFIG[activeKind.value].steps[progress.value.step - 1];
      if (!config?.navigateOnClick) return;
      if (newPath === config.navigateOnClick) advance();
    },
  );

  const currentStepRoute = computed(() => {
    const config = CONFIG[activeKind.value].steps[progress.value.step - 1];
    if (!config) return null;
    return config.route ?? config.navigateOnClick ?? null;
  });

  return {
    progress,
    activeKind,
    isLoaded,
    isFloatingExpanded,
    highlightedSidebarItems,
    stopModalVisible,
    stopModalAction,
    currentStepConfig,
    currentStepRoute,
    isActive,
    isModalStep,
    isComplete,
    totalSteps,
    allowedSidebarItems,
    canGoBack,
    readyToAdvance,
    workProgress,
    workTotalSteps,
    isWorkComplete,
    isWorkAutoLaunchSuppressed,
    init,
    advance,
    goBack,
    pause,
    resume,
    skipAll,
    resetProgress,
    resumeWork,
    resetWorkProgress,
    launchWork,
    setSecondaryHighlightActive,
    reset,
  };
});
